import React, { useState, useEffect } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Card,
  CardContent,
  Typography,
  Divider,
  TableSortLabel,
  TextField,
  useTheme,
  Grid,
  MenuItem,
  Button,
} from "@mui/material";
import Icons from "../../Utils/Icons";
import { GET } from "../Generic/CrudRequest";
import { searchPermissions } from "../Users/PermissionsUser/PermissionsActions";
import useExcelExport from "../../Utils/useExcelExport";
import { Language, SaveAlt } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

export default function Locations(props) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [rows, setRows] = React.useState([]);
  const [orderBy, setOrderBy] = React.useState("");
  const [column, setColumn] = React.useState(0);
  const [order, setOrder] = React.useState("asc");
  const [totalRecords, setTotalRecords] = React.useState(0);
  const [searchText, setSearchText] = useState("");
  const [searchName, setSearchName] = useState("");
  const [estatus, setEstatus] = React.useState(true);
  const [tiposUbicacion, setTiposUbicacion] = useState([]);
  const [tipoUbicacion, setTipoUbicacion] = useState(0);
  const [register, setRegister] = useState(false);
  const [edit, setEdit] = useState(false);
  const theme = useTheme();
  const { config } = props;
  const exportToExcel = useExcelExport();
  const { t, i18n } = useTranslation();
  const language = localStorage.getItem("language");

  const handleExport = () => {
    exportToExcel(rows, columns, t("locations.excelTitle"));
  };

  const columns = [
    { id: "codigo", label: t("locations.code"), minWidth: "100px" },
    {
      id: "idUbicacionTipoNavigation",
      label: t("locations.locationType"),
      minWidth: "100px",
    },
    { id: "nombre", label: t("locations.name"), minWidth: "100px" },
    { id: "colonia", label: t("locations.table.neighborhood"), minWidth: "100px" },
    { id: "calle", label: t("locations.table.street"), minWidth: "100px" },
    { id: "numeroExterior", label: t("locations.table.exteriorNumber"), minWidth: "100px" },
    { id: "codigoPostal", label: t("locations.table.postalCode"), minWidth: "100px" },
    { id: "ciudad", label: t("locations.table.city"), minWidth: "100px" },
    { id: "estado", label: t("locations.table.state"), minWidth: "100px" },
    { id: "numeroTelefono", label: t("locations.table.phone"), minWidth: "100px" },
  ];

  useEffect(() => {
    fetchLocations();
    fetchLocationsType();
  }, [page, rowsPerPage, column, order, searchText, searchName, searchText, tipoUbicacion, estatus]);

  // Escuchar cambios de idioma para recargar los datos
  useEffect(() => {
    const handleLanguageChange = () => {
      // Recargar solo los datos sin recargar toda la página
      fetchLocations();
      fetchLocationsType();
    };

    // Agregar listener para el evento de cambio de idioma desde i18n
    i18n.on('languageChanged', handleLanguageChange);

    // Cleanup: remover listener cuando el componente se desmonte
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const fetchLocations = async () => {
    const dataLocations = await GET(`${config.url}/api/Location/paginado`, {
      start: page * rowsPerPage,
      length: rowsPerPage,
      clave: searchText,
      idTipoUbicacion: tipoUbicacion,
      nombre: searchName,
      status: estatus,
      column: column,
      dir: order,
    });

    setRows(dataLocations.resultado);
    setTotalRecords(dataLocations.totalRegistros);

    const permissionsArray = JSON.parse(localStorage.getItem("permissions"));

    const permissionRegister = searchPermissions(
      "Ubicaciones",
      "Registrar",
      permissionsArray
    );

    const permissionEdit = searchPermissions(
      "Ubicaciones",
      "Editar",
      permissionsArray
    );

    setRegister(permissionRegister);
    setEdit(permissionEdit);
  };

  const fetchLocationsType = async () => {
    const tiposUbicacionData = await GET(`${config.url}/api/LocationType`);
    setTiposUbicacion(tiposUbicacionData);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleRequestSort = (property, columnNumber) => {
    const isAsc = orderBy === property && order === "asc";
    setColumn(columnNumber);
    setOrderBy(property);
    setOrder(isAsc ? "desc" : "asc");
  };

  const handleSearch = () => {
    fetchLocations();
  };

  const handleClear = () => {
    setSearchText("");
    setSearchName("");
    setEstatus(true);
    setTipoUbicacion(0);
    // La búsqueda se ejecutará automáticamente gracias al useEffect
  };

  const handleChangeEstatus = (event) => {
    setEstatus(event.target.value);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        overflow: "hidden",
        boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
        border: "1px solid rgba(0, 0, 0, 0.1)",
        borderRadius: 2,
      }}
    >
      <CardContent>
        <CardContent
          sx={{ display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          justifyContent: "space-between", 
          overflow: "hidden", 
          maxWidth: "100%", }}
        >
          <Typography variant="h3" sx={{ flex: "1", fontWeight: "bold" }}>
            {t("locations.title")}
          </Typography>
          {register && (
            <Icons
              iconName="Add"
              fontSize="large"
              to="/Ubicaciones/Agregar"
              style={{
                marginLeft: "auto",
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.white.main,
              }}
              tooltip={t("buttons.add")}
            />
          )}
        </CardContent>
        <Divider sx={{ maxWidth: "calc(100% - 32px)", margin: "auto" }} />
        <CardContent
          sx={{
            maxWidth: "calc(100% - 32px)",
            margin: "auto",
            alignItems: "auto",
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                label={t("locations.code")}
                type="search"
                InputLabelProps={{
                  shrink: true,
                }}
                variant="standard"
                size="small"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{
                  width: 400,
                  maxWidth: "100%",
                  color: theme.palette.primary.main,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label={t("locations.locationType")}
                InputLabelProps={{
                  shrink: true,
                }}
                variant="standard"
                size="small"
                value={tipoUbicacion}
                onChange={(e) => setTipoUbicacion(e.target.value)}
                fullWidth
              >
                <MenuItem key={0} value={0}>
                  {t("select")}
                </MenuItem>
                {tiposUbicacion.map((tipo) => (
                  <MenuItem
                    key={tipo.idUbicacionTipo}
                    value={tipo.idUbicacionTipo}
                  >
                  {language == "es" ? tipo.descripcion : tipo.descripcionIngles}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label={t("locations.name")}
                type="search"
                InputLabelProps={{
                  shrink: true,
                }}
                variant="standard"
                size="small"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                sx={{
                  width: 400,
                  maxWidth: "100%",
                  color: theme.palette.primary.main,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label={t("locations.status")}
                InputLabelProps={{
                  shrink: true,
                }}
                name="estatus"
                variant="standard"
                size="small"
                value={estatus}
                fullWidth
                onChange={handleChangeEstatus}
              >
                <MenuItem value={true}>{t("locations.active")}</MenuItem>
                <MenuItem value={false}>{t("locations.inactive")}</MenuItem>
              </TextField>
            </Grid>
            <Icons
              iconName="Search"
              fontSize="large"
              onClick={handleSearch}
              style={{ color: theme.palette.primary.main }}
              tooltip={t("buttons.search")}
            />
            <Icons
              iconName="Clear"
              fontSize="large"
              onClick={handleClear}
              style={{ color: theme.palette.primary.main }}
              tooltip={t("buttons.clear")}
            />
          </Grid>
        </CardContent>
        <Paper
          sx={{
            maxWidth: "calc(100% - 32px)",
            margin: "auto",
            overflow: "hidden",
            boxShadow: "none",
            border: "none",
          }}
        >
          <TableContainer>
            <Button
              variant="contained"
              startIcon={<SaveAlt />}
              onClick={handleExport}
            >
              {t("buttons.exportExcel")}
            </Button>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align="left"
                      style={{ minWidth: column.minWidth, fontWeight: "bold" }}
                    >
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : "asc"}
                        onClick={() =>
                          handleRequestSort(column.id, columns.indexOf(column))
                        }
                      >
                        {column.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} align="center">
                      {t("locations.noLocationsAvailable")}
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row, index) => {
                  return (
                    <TableRow key={index}>
                      {columns.map((column) => (
                        <TableCell key={column.id} align="left">
                          {column.id === "idUbicacionTipoNavigation" ? (
                            row.idUbicacionTipoNavigation &&
                            row.idUbicacionTipoNavigation.descripcion !== "" ? (
                              language === "es" 
                                ? row.idUbicacionTipoNavigation.descripcion 
                                : row.idUbicacionTipoNavigation.descripcionIngles
                            ) : (
                              <i>{t("pagination.notAvailable")}</i>
                            )
                          ) : row[column.id] && row[column.id] !== "" ? (
                            row[column.id]
                          ) : (
                            <i>{t("pagination.notAvailable")}</i>
                          )}
                        </TableCell>
                      ))}
                      <TableCell align="right">
                        {edit && (
                          <Icons
                            iconName="Edit"
                            fontSize="medium"
                            style={{
                              backgroundColor: theme.palette.primary.main,
                              color: "#fff",
                            }}
                            to={`/Ubicaciones/Editar/${row.idUbicacion}`}
                            tooltip={t("buttons.edit")}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 100, { value: -1, label: t("pagination.all") }]}
            component="div"
            count={totalRecords}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t("pagination.labelRowsPerPage")}
            labelDisplayedRows={({ from, to, count }) =>
              t("pagination.displayedRows", { from, to, count })
            }
          />
        </Paper>
      </CardContent>
    </Card>
  );
}
